import { useCallback, useRef, useState } from 'react'
import OtpInput from 'react-otp-input'

import './text-verification.scss'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { Button } from '@Components/button/button'
import PreviousChevron from '@Assets/icons/previous-chevron.svg'
import { ClaimReservationCommunicationMethod } from '@interaction-gaming/player-data-service-browser-client'
import { PlayerDataServiceAPI } from '@API/data-services/player-data-service.ts'

const CLASS_NAME = 'text-verification-screen'

type VerificationProps = {
  phoneNumber: string
  emailAddress: string
  contactMethod: ClaimReservationCommunicationMethod
  errorMessage?: string
  onBack: () => void
  onNext: (
    phoneNumber: string,
    emailAddress: string,
    contactMethod: ClaimReservationCommunicationMethod,
    code: string,
  ) => void
}

const CODE_LENGTH = 6
export const VerificationScreen = ({
  phoneNumber,
  emailAddress,
  contactMethod,
  errorMessage,
  onNext,
  onBack,
}: VerificationProps) => {
  const [otp, setOtp] = useState('')
  const submitButtonRef = useRef<HTMLButtonElement>(null)

  const resendCode = useCallback(() => {
    PlayerDataServiceAPI.sendVerification(phoneNumber, emailAddress, contactMethod)
  }, [contactMethod, emailAddress, phoneNumber])

  const otpChangeHandler = useCallback((value: string) => {
    if (
      value.length === CODE_LENGTH &&
      document.activeElement === document.getElementById(`otp-input-${CODE_LENGTH - 1}`)
    ) {
      setTimeout(() => {
        submitButtonRef.current?.focus()
      }, 50)
    }
    setOtp(value)
  }, [])

  return (
    <section className={`${CLASS_NAME}`}>
      <p className={`${CLASS_NAME}__text`}>
        For your security, we need to ensure it’s really you. Please enter the 6-digit code we sent
        to{' '}
        <strong>
          {contactMethod === ClaimReservationCommunicationMethod.EMAIL ? emailAddress : phoneNumber}
        </strong>
        . This code expires in 30 mins.
      </p>
      <OtpInput
        containerStyle={`${CLASS_NAME}__otp-container`}
        value={otp}
        onChange={otpChangeHandler}
        inputStyle={`${CLASS_NAME}__otp-input ${errorMessage ? `${CLASS_NAME}__otp-error` : ''}`}
        numInputs={CODE_LENGTH}
        shouldAutoFocus
        renderSeparator={<span>&nbsp;&nbsp;&nbsp;</span>}
        renderInput={(props, index) => {
          return <input id={`otp-input-${index}`} {...props} />
        }}
      />

      <p className={`${CLASS_NAME}__otp-validation-error`}>{errorMessage ? errorMessage : null}</p>

      <button className={`${CLASS_NAME}__text-button`} onClick={() => resendCode()}>
        Didn't get it? Resend code
      </button>

      <FormFloatingFooter>
        <section className={`floating-footer__buttons`}>
          <Button
            className={`floating-footer__next-button`}
            ref={submitButtonRef}
            disabled={otp.length !== CODE_LENGTH}
            onClick={() => onNext(phoneNumber, emailAddress, contactMethod, otp)}>
            Next
          </Button>
          <Button
            className={`floating-footer__previous-button`}
            onClick={onBack}
            aria-label="Go back to previous step">
            <img src={PreviousChevron} alt="" aria-hidden="true" />
          </Button>
        </section>
      </FormFloatingFooter>
    </section>
  )
}
